import { useCallback, useEffect, useState } from 'react';

import { keyframes, css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Image } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

export type AnimatedModalContent = {
	heading?: string;
	text?: string;
	image?: string;
	icon?: string;
};

export type AnimatedModalProps = {
	content: AnimatedModalContent;
	onClose: () => void;
};

const animationDuration = '1.5s';

const contentRevealingKeyframes = keyframes({
	'0%, 70%': {
		clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 0)',
	},
	'100%': {
		clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
	},
});

const expandingWhiteBackgroundKeyframes = keyframes({
	'0%, 35%': {
		width: '300px',
		height: '100px',
	},
	'70%': {
		width: '100%',
		height: '100px',
	},
	'100%': {
		width: '100%',
		height: '100%',
	},
});

const dialogShiftingDownwardKeyframes = keyframes({
	'0%': {
		opacity: 0,
		transform: 'translateY(-40px)',
	},
	'15%': {
		opacity: 1,
		transform: 'translateY(-40px)',
	},
	'70%': {
		opacity: 1,
		transform: 'translateY(-40px)',
	},
	'100%': {
		opacity: 1,
		transform: 'translateY(0)',
	},
});

const modalTopBarKeyframes = keyframes({
	'0%, 35%': {
		width: '250px',
	},
	'70%, 100%': {
		width: '100%',
	},
});
const modalBackgroundStyle = css({
	position: 'fixed',
	inset: 0,
	zIndex: 10,
});

const modalBackgroundOverlayStyle = css({
	height: '100vh',
	width: '100vw',
	padding: 0,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	overflow: 'auto',
});

const modalDialogStyle = css({
	position: 'relative',
	margin: `${token('space.300')} auto`,
	zIndex: 1,
	padding: token('space.400'),
	maxWidth: '768px',
	width: '90%',
	animationFillMode: 'forwards',
	animationName: dialogShiftingDownwardKeyframes,
	animationDuration: animationDuration,
	animationTimingFunction: wacToken('motion.easing.quick-entrance'),
});

const modalTopBarStyle = css({
	position: 'relative',
	margin: '0 auto',
	padding: `0 0 ${token('space.250')}`,
	width: '100%',
	animationFillMode: 'forwards',
	animationName: modalTopBarKeyframes,
	animationDuration: animationDuration,
	animationTimingFunction: wacToken('motion.easing.quick-entrance'),
});

const expandingWhiteBackgroundStyle = css({
	zIndex: -1,
	position: 'absolute',
	inset: 0,
	borderRadius: wacToken('corner.radius.card'),
	margin: '0 auto',
	animationName: expandingWhiteBackgroundKeyframes,
	animationDuration: animationDuration,
	animationTimingFunction: wacToken('motion.easing.quick-entrance'),
	backgroundColor: wacToken('color.neutral.light0'),
	boxShadow: `5px 8px 32px -10px ${wacToken('color.neutral.light800')}`,
});

const contentRevealingStyle = css({
	clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
	animationName: contentRevealingKeyframes,
	animationDuration: animationDuration,
	animationTimingFunction: wacToken('motion.easing.quick-entrance'),
});

const headingStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: 16,
	margin: `0 0 ${token('space.200')}`,
	fontSize: 20,
	fontWeight: 600,
});

const closeButtonStyle = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineEnd: 0,
	background: 'none',
	border: 'none',
	fontSize: token('space.250'),
	cursor: 'pointer',
	color: wacToken('color.neutral.light800'),
	lineHeight: 1,
	padding: token('space.050'),
});

const modalBackgroundOverlayButtonStyle = css({
	appearance: 'none',
	position: 'absolute',
	inset: 0,
	border: 'none',
	padding: 0,
	margin: 0,
	cursor: 'pointer',
	transition: 'background-color 0.3s ease',
});

export const AnimatedModal = ({ content, onClose }: AnimatedModalProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isClosing, setIsClosing] = useState(false);

	useEffect(() => {
		requestAnimationFrame(() => setIsOpen(true));
	}, []);

	const handleClose = useCallback(() => {
		setIsClosing(true);
		setTimeout(() => onClose?.(), 250);
	}, [onClose]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape') handleClose();
		},
		[handleClose],
	);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown]);

	const entered = isOpen && !isClosing;

	return (
		<div css={modalBackgroundStyle} aria-hidden={!entered}>
			<div css={modalBackgroundOverlayStyle}>
				<button
					css={modalBackgroundOverlayButtonStyle}
					type="button"
					aria-label="Close modal"
					onClick={handleClose}
					style={{
						// eslint-disable-next-line -- scrim overlay; no scrim token for rgba
						backgroundColor: entered ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
					}}
				/>
				<div
					css={modalDialogStyle}
					role="dialog"
					aria-modal="true"
					aria-label={content.heading ?? 'Modal'}
					aria-labelledby="anim-modal-heading"
				>
					<div css={expandingWhiteBackgroundStyle} />
					<div css={modalTopBarStyle}>
						<button
							css={closeButtonStyle}
							type="button"
							onClick={handleClose}
							aria-label="Close modal"
						>
							&times;
						</button>

						{content.heading && (
							<h3 id="anim-modal-heading" css={headingStyle}>
								{content.icon && (
									<Image src={content.icon} alt={content.heading ?? ''} width={24} height={24} />
								)}

								{content.heading}
							</h3>
						)}
					</div>

					<div css={contentRevealingStyle}>
						{content.text && <p>{content.text}</p>}

						{content.image && <Image src={content.image} alt={content.heading ?? ''} />}
					</div>
				</div>
			</div>
		</div>
	);
};
